import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Lock, Plus, RefreshCw, Unlock } from 'lucide-react';
import { MainLayout } from '@/components/layout/MainLayout';
import {
  Button,
  EmptyState,
  FilterSelect,
  LoadingState,
  Modal,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TenantSelector,
} from '@/components/ui';
import { broadcastSchedulesApi, type BroadcastSchedule, type BroadcastScheduleRun } from '@/api/broadcastSchedules';
import { useTenantScope } from '@/hooks/useTenantScope';
import { usePermissions } from '@/hooks/usePermissions';
import { useToast } from '@/hooks/useToast';
import { getChannelLabel } from '@/config/channels';
import { formatDate } from '@/utils/formatters';

const ACTIVE_OPTIONS = [
  { value: '', label: 'Все' },
  { value: 'true', label: 'Активные' },
  { value: 'false', label: 'Выключенные' },
];

function segmentTypeLabel(type?: string) {
  switch (type) {
    case 'dynamic':
      return 'Динамический';
    case 'lk':
      return 'ЛК';
    default:
      return 'Обычный';
  }
}

function runStatusLabel(status?: string) {
  switch (status) {
    case 'completed':
      return 'Завершен';
    case 'blocked':
      return 'Заблокирован';
    case 'failed':
      return 'Ошибка';
    case 'running':
      return 'Выполняется';
    default:
      return status || '—';
  }
}

export function BroadcastSchedulesPage() {
  const navigate = useNavigate();
  const { can } = usePermissions();
  const { showError, showSuccess } = useToast();
  const { tenantId, isSuperAdmin, selectedTenantId, setSelectedTenant, requireTenantSelection } = useTenantScope();

  const [items, setItems] = useState<BroadcastSchedule[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('');
  const [runsModalSchedule, setRunsModalSchedule] = useState<BroadcastSchedule | null>(null);
  const [runs, setRuns] = useState<BroadcastScheduleRun[]>([]);
  const [runsLoading, setRunsLoading] = useState(false);

  const canWrite = can('campaigns:write');

  const loadData = async () => {
    if (!tenantId) {
      setItems([]);
      return;
    }
    try {
      setLoading(true);
      const response = await broadcastSchedulesApi.list({
        tenant_id: tenantId,
        is_active: activeFilter === '' ? undefined : activeFilter === 'true',
        limit: 100,
      });
      setItems(response.schedules);
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось загрузить регулярные рассылки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadData();
  }, [tenantId, activeFilter]);

  const openRuns = async (schedule: BroadcastSchedule) => {
    setRunsModalSchedule(schedule);
    setRuns([]);
    try {
      setRunsLoading(true);
      const response = await broadcastSchedulesApi.listRuns(schedule.id, tenantId);
      setRuns(response.runs);
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось загрузить историю запусков');
    } finally {
      setRunsLoading(false);
    }
  };

  const handleDelete = async (schedule: BroadcastSchedule) => {
    if (!tenantId || !window.confirm(`Удалить расписание "${schedule.name}"?`)) return;
    try {
      await broadcastSchedulesApi.remove(schedule.id, tenantId);
      showSuccess('Расписание удалено');
      await loadData();
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось удалить расписание');
    }
  };

  const handleToggleActive = async (schedule: BroadcastSchedule) => {
    if (!tenantId) return;
    try {
      await broadcastSchedulesApi.update(schedule.id, { is_active: !schedule.is_active }, tenantId);
      showSuccess(schedule.is_active ? 'Расписание выключено' : 'Расписание включено');
      await loadData();
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось обновить расписание');
    }
  };

  const handleToggleLock = async (schedule: BroadcastSchedule) => {
    if (!tenantId) return;
    try {
      await broadcastSchedulesApi.setLaunchLocked(schedule.id, !schedule.launch_locked, tenantId);
      showSuccess(!schedule.launch_locked ? 'Запуск заблокирован' : 'Запуск разблокирован');
      await loadData();
    } catch (e: any) {
      showError(e?.response?.data?.error || 'Не удалось обновить блокировку запуска');
    }
  };

  const rows = useMemo(() => items, [items]);

  return (
    <MainLayout
      headerTitle="Регулярные рассылки"
      headerActions={(
        <>
          {canWrite && (
            <Button variant="primary" onClick={() => navigate('/broadcast-schedules/create')}>
              <Plus className="w-4 h-4 mr-2" />
              Новая регулярная рассылка
            </Button>
          )}
          <Button variant="secondary" onClick={() => void loadData()} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Обновить
          </Button>
        </>
      )}
    >
      <div className="space-y-3 p-4">
        <div className="flex gap-4">
          {isSuperAdmin && (
            <TenantSelector
              value={selectedTenantId ?? ''}
              onChange={setSelectedTenant}
            />
          )}
          <div className="w-48">
            <FilterSelect
              value={activeFilter}
              onChange={setActiveFilter}
              options={ACTIVE_OPTIONS}
              placeholder="Все"
            />
          </div>
        </div>

        <div className="rounded-xl border border-shell-border bg-shell-surface">
          <p className="border-b border-shell-border px-4 py-3 text-sm font-semibold text-shell-strong">
            Расписания ({items.length})
          </p>
          <div className="p-3">
            {requireTenantSelection ? (
              <EmptyState
                type="noData"
                title="Выберите клинику"
                description="Для работы с регулярными рассылками выберите клинику."
              />
            ) : loading ? (
              <LoadingState text="Загрузка регулярных рассылок..." />
            ) : rows.length === 0 ? (
              <EmptyState
                type="noData"
                title="Нет регулярных рассылок"
                description="Создайте первую recurring сегментную рассылку."
                actionText="Создать"
                onAction={() => canWrite && navigate('/broadcast-schedules/create')}
              />
            ) : (
              <Table>
                <TableHead>
                  <TableRow>
                    <TableHeader>Название</TableHeader>
                    <TableHeader>Сегмент</TableHeader>
                    <TableHeader>Шаблон</TableHeader>
                    <TableHeader>Расписание</TableHeader>
                    <TableHeader>Статус</TableHeader>
                    <TableHeader>Последний запуск</TableHeader>
                    <TableHeader>Действия</TableHeader>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {rows.map((item) => (
                    <TableRow key={item.id}>
                      <TableCell className="font-medium">{item.name}</TableCell>
                      <TableCell>
                        <div>{item.config.segment_name || `#${item.config.segment_id}`}</div>
                        <div className="text-xs text-neutral-500">{segmentTypeLabel(item.config.segment_type)}</div>
                      </TableCell>
                      <TableCell>
                        <div>{item.config.template_name || item.config.template_id}</div>
                        <div className="text-xs text-neutral-500">
                          {item.config.channel_override ? getChannelLabel(item.config.channel_override) : 'Авто по patient preference / tenant priority'}
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>{item.schedule_type === 'cron' ? item.cron_expr : item.send_time}</div>
                        <div className="text-xs text-neutral-500">{item.timezone}</div>
                        {item.next_run_at && (
                          <div className="text-xs text-primary-700">Следующий: {formatDate(item.next_run_at)}</div>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className={item.is_active ? 'text-emerald-700' : 'text-neutral-500'}>
                          {item.is_active ? 'Активна' : 'Выключена'}
                        </div>
                        <div className={item.launch_locked ? 'text-red-700 text-xs' : 'text-xs text-neutral-500'}>
                          {item.launch_locked ? 'Запуск заблокирован' : 'Запуск разрешён'}
                        </div>
                      </TableCell>
                      <TableCell>{item.last_run_at ? formatDate(item.last_run_at) : '—'}</TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-2">
                          <button className="text-primary-600 hover:underline text-sm" onClick={() => navigate(`/broadcast-schedules/${item.id}/edit`)}>
                            Изменить
                          </button>
                          <button className="text-primary-600 hover:underline text-sm" onClick={() => void openRuns(item)}>
                            История
                          </button>
                          <button className="text-primary-600 hover:underline text-sm" onClick={() => void handleToggleActive(item)}>
                            {item.is_active ? 'Выключить' : 'Включить'}
                          </button>
                          {isSuperAdmin && (
                            <button className="text-primary-600 hover:underline text-sm" onClick={() => void handleToggleLock(item)}>
                              {item.launch_locked ? (
                                <span className="inline-flex items-center gap-1"><Unlock className="w-3 h-3" /> Разблокировать</span>
                              ) : (
                                <span className="inline-flex items-center gap-1"><Lock className="w-3 h-3" /> Заблокировать</span>
                              )}
                            </button>
                          )}
                          <button className="text-red-600 hover:underline text-sm" onClick={() => void handleDelete(item)}>
                            Удалить
                          </button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </div>
        </div>
      </div>

      <Modal
        isOpen={Boolean(runsModalSchedule)}
        onClose={() => setRunsModalSchedule(null)}
        title={runsModalSchedule ? `История запусков: ${runsModalSchedule.name}` : 'История запусков'}
        size="lg"
      >
        {runsLoading ? (
          <LoadingState text="Загрузка запусков..." size="sm" inline />
        ) : runs.length === 0 ? (
          <div className="text-sm text-neutral-500">Запусков пока нет.</div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Когда</TableHeader>
                <TableHeader>Статус</TableHeader>
                <TableHeader>Аудитория</TableHeader>
                <TableHeader>Materialized</TableHeader>
                <TableHeader>Очередь/Send</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {runs.map((run) => (
                <TableRow key={run.id}>
                  <TableCell>{formatDate(run.started_at)}</TableCell>
                  <TableCell>
                    <div>{runStatusLabel(run.status)}</div>
                    {run.error && <div className="text-xs text-red-600">{run.error}</div>}
                  </TableCell>
                  <TableCell>{run.audience_total}</TableCell>
                  <TableCell>{run.materialized_count}</TableCell>
                  <TableCell>
                    <div>queue: {run.queued_count}</div>
                    <div className="text-xs text-neutral-500">sent: {run.sent_count} / failed: {run.failed_count} / skipped: {run.skipped_count}</div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Modal>
    </MainLayout>
  );
}
